import './copy-button.scss';

import classnames from 'classnames';
import React, {ReactNode, useState} from 'react';
import { FormattedMessage } from 'react-intl';
import DefaultButton from '../default-button/default-button';

export interface ButtonProp {
    copyValue: unknown;
    className?: string;
    disabled?: boolean;
    withoutIcon?: boolean;
    withCopyLink?: boolean;
    text?: string | ReactNode;
    onCopy?: ()=>void;
    copyAsHTML?: boolean;
}

export interface ButtonDefProp {
    onClick: () => void;
    value: string;
    disabled?: boolean;
    withoutIcon?: boolean;
    withCopyLink?: boolean;
}

const CloneButton = ({
    onClick, disabled = false, value
}: ButtonDefProp) => {
    const [isCloned, setIsCloned] = useState(false);
    const copyToClipboard = () => {
        return navigator.clipboard.writeText(`${value}`).then(() => setIsCloned(true));
    };

    const handleClick = () => {
        onClick();
        copyToClipboard();
    };
    return (
        <div className='copy-button'>
            <DefaultButton
                disabled={disabled}
                onClick={handleClick}
                className='small-button m-left-10px'
                text={
                    <FormattedMessage
                        id='oe.common.clone'
                        description='Helper text'
                        defaultMessage='Clone'
                    />
                }
            />
            <div className='copy-button_tooltip'>
                {isCloned ? (
                    <FormattedMessage id='oe.common.cloned' description='Helper text' defaultMessage='Cloned' />
                ) : (
                    <FormattedMessage id='oe.common.clone' description='Helper text' defaultMessage='Clone' />
                )}
            </div>
        </div>
    );
};

const CopyButton = ({
    copyValue,
    className = '',
    disabled = false,
    withoutIcon = false,
    withCopyLink,
    onCopy,
    text,
    copyAsHTML,
}: ButtonProp) => {
    const [isCopied, setIsCopied] = useState(false);
    const [tooltipBelow, setTooltipBelow] = useState(false);
    const buttonRef = React.useRef<HTMLSpanElement>(null);
    const isDisabled = disabled || !copyValue;

    const afterCopyHandler = () => {
        if (buttonRef.current) {
            const { top } = buttonRef.current.getBoundingClientRect();
            setTooltipBelow(top < 100);
        }
        setIsCopied(true);
        onCopy?.();
    };

    const onCopyHandler = () => {
        if (copyAsHTML) {
            navigator.clipboard.write([
              new ClipboardItem({
                'text/plain': new Blob([copyValue as string], { type: 'text/plain' }),
                'text/html': new Blob([copyValue as string], { type: 'text/html' }),
              }),
            ]).then(afterCopyHandler);
        } else {
            navigator.clipboard.writeText(`${copyValue}`).then(afterCopyHandler);
        }
    };

    return (
        <span
            ref={buttonRef}
            className={classnames('copy-button text-blue cursor-point text-hover-light-blue',
                className,
                { 'copy-button-disabled': isDisabled },
                { 'material-icons font-size-18px': !withoutIcon },
                { 'copy-button-without-icon': withoutIcon }
            )}
            {...(!isDisabled && {
                onClick: onCopyHandler,
                ...(isCopied && { onMouseOut: () => { setIsCopied(false); setTooltipBelow(false); } }),
            })}
        >
            {withoutIcon ?
                withCopyLink ?
                    (text || <FormattedMessage
                        id='oe.common.copyLink'
                        description='Helper text'
                        defaultMessage={'Copy Link'}
                    />)
                    : (text ||
                        <FormattedMessage
                            id='oe.common.copy'
                            description='Helper text'
                            defaultMessage={'Copy'}
                        />) : 'file_copy'
            }
            {(!withoutIcon || withoutIcon && isCopied) && (
                <div className={classnames('copy-button_tooltip', { 'copy-button_tooltip--below': tooltipBelow })}>
                    {isCopied ? (
                        <FormattedMessage id='oe.common.copied' description='Helper text' defaultMessage='Copied' />
                    ) : (
                        text || <FormattedMessage id='oe.common.copy' description='Helper text' defaultMessage={'Copy'} />
                    )}
                </div>
            )}
        </span>
    );
};

export { CopyButton, CloneButton };
